import React, { useState } from 'react';
import styled from 'styled-components';
import Header from './Header';
import Footer from './Footer';
import { motion, AnimatePresence } from 'framer-motion';
import NewsTab from './tabs/NewsTab';
import BackgroundTab from './tabs/BackgroundTab';
import TipsTab from './tabs/TipsTab';
import ToolsTab from './tabs/ToolsTab';

// Tab Components
// ToolsTab component moved to src/components/tabs/ToolsTab.js

// TipsTab component moved to src/components/tabs/TipsTab.js

// BackgroundTab component moved to src/components/tabs/BackgroundTab.js

const PostSessionNotes = () => {
  const [activeTab, setActiveTab] = useState('background');

  const tabs = [
    { id: "background", label: "Background" },
    { id: "tips", label: "Tips" },
    { id: "tools", label: "Tools" },
    { id: "news", label: "Resources" }
  ];

  // Define animations for tab transitions
  const tabContentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.4,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    },
    exit: { 
      opacity: 0, 
      y: -20,
      transition: { duration: 0.2 }
    }
  };

  // Keyboard accessibility for tab navigation
  const handleKeyDown = (e, tab) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setActiveTab(tab);
    }
  };

  return (
    <div>
      <Header />
      <Content>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <TitleSection>
            <h1>Post Session Notes</h1>
            <ThankYouMessage>
              <h2>Thank you for taking the course</h2>
              <p>Bookmark this URL, it will be kept up to date with the latest tools and best practices</p>
            </ThankYouMessage>
          </TitleSection>
        </motion.div>

        <TabsContainer role="tablist" aria-label="Content Categories">
          {tabs.map((tab) => (
            <TabButton
              key={tab.id}
              role="tab"
              id={`tab-${tab.id}`}
              aria-selected={activeTab === tab.id}
              aria-controls={`panel-${tab.id}`}
              active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              onKeyDown={(e) => handleKeyDown(e, tab.id)}
              tabIndex={activeTab === tab.id ? 0 : -1}
            >
              {tab.label}
            </TabButton>
          ))}
        </TabsContainer>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            variants={tabContentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            role="tabpanel"
            id={`panel-${activeTab}`}
            aria-labelledby={`tab-${activeTab}`}
            tabIndex={0}
          >
            {activeTab === 'tools' && <ToolsTab />}
            {activeTab === 'tips' && <TipsTab />}
            {activeTab === 'background' && <BackgroundTab />}
            {activeTab === 'news' && <NewsTab />}
          </motion.div>
        </AnimatePresence>
      </Content>
      <Footer />
    </div>
  );
};

// Styled Components
const Content = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const TitleSection = styled.div`
  margin-bottom: 2rem;
  text-align: center;
  
  h1 {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: var(--primary-color);
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
`;

const ThankYouMessage = styled.div`
  margin-bottom: 2rem;
  
  h2 {
    font-size: 1.8rem;
    margin-bottom: 0.5rem;
    color: var(--dark-blue);
    
    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }
  
  p {
    font-size: 1.2rem;
    color: var(--text-color);
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

const TabsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    overflow-x: auto;
    padding-bottom: 0.5rem;
    -webkit-overflow-scrolling: touch;
    justify-content: flex-start;
  }
`;

const TabButton = styled.button`
  padding: 0.6rem 1.2rem;
  background: ${props => props.active ? 'var(--primary-color)' : 'var(--lightest-gray)'};
  color: ${props => props.active ? 'white' : 'var(--dark-blue)'};
  border: none;
  border-radius: 20px;
  font-weight: ${props => props.active ? 'bold' : 'normal'};
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? 'var(--primary-color)' : 'var(--light-gray)'};
  }
  
  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
    flex-shrink: 0;
  }
`;

const TabContainer = styled.div`
  min-height: 50vh;
`;

const Section = styled.div`
  margin-bottom: 3rem;
  
  h2 {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
    color: var(--primary-color);
    text-align: center;
    
    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }
  
  h3 {
    font-size: 1.4rem;
    margin: 1.5rem 0 1rem;
    color: var(--dark-blue);
    
    @media (max-width: 768px) {
      font-size: 1.2rem;
    }
  }
  
  p {
    margin-bottom: 1rem;
    line-height: 1.6;
    
    @media (max-width: 768px) {
      font-size: 0.95rem;
    }
  }
  
  ul {
    padding-left: 1.5rem;
    margin-bottom: 1rem;
  }
  
  li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
    
    @media (max-width: 768px) {
      font-size: 0.95rem;
    }
  }
`;

// PromptsSection, PromptGuideContainer, PromptGuideItem, PromptTipsContainer,
// PromptTip, PromptExamplesContainer, PromptExample, PitfallsList
// moved to src/components/tabs/styles/TipsTabStyles.js

// Add new styled component for section descriptions
const SectionDescription = styled.p`
  text-align: center;
  color: var(--text-color);
  font-size: 1.1rem;
  margin: -0.5rem 0 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin: -0.25rem 0 1.5rem;
  }
`;

export default PostSessionNotes;
